import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Boxes,
  DollarSign,
  Layers3,
  RefreshCw,
  Tags,
} from "lucide-react";

import EmptyState from "@/components/layout/EmptyState";
import MetricCard from "@/components/layout/MetricCard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import type { Asset } from "@/lib/api-types";
import { formatRenderCollectionLabel, formatRenderPrice } from "@/lib/render-catalog";

const numberFormatter = new Intl.NumberFormat("vi-VN");

function formatPercent(value: number): string {
  return `${value.toFixed(value >= 100 ? 0 : 1)}%`;
}

function getRecentUploadTitle(asset: Asset): string {
  const name = asset.name.trim();
  if (!name || name.startsWith("-")) {
    const componentLabel = asset.componentType === "prop" ? "Prop" : "Component";
    return `${componentLabel} ${asset.componentId} / drawable ${asset.drawableId}`;
  }
  return name;
}

function buildRankingEntries(
  counts: Record<string, number> | undefined,
  limit: number,
): Array<{ label: string; value: number }> {
  return Object.entries(counts ?? {})
    .map(([label, value]) => ({
      label: formatRenderCollectionLabel(label),
      value,
    }))
    .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
    .slice(0, limit);
}

function RankingList({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: Array<{ label: string; value: number }>;
}) {
  const maxValue = items.reduce((current, item) => Math.max(current, item.value), 0);

  return (
    <Card className="border-border/80 bg-card/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <EmptyState
            title="Chưa có dữ liệu"
            description="Dữ liệu tổng hợp sẽ xuất hiện khi catalog và pricing bắt đầu có bản ghi thực."
            className="p-6"
          />
        ) : (
          items.map((item) => {
            const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 8) : 0;
            return (
              <div key={item.label} className="space-y-2 rounded-2xl border border-border/80 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{item.label}</span>
                  <span className="text-muted-foreground">
                    {numberFormatter.format(item.value)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary/80" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ["stats-overview"],
    queryFn: api.getStats,
  });

  const topCollections = useMemo(
    () => buildRankingEntries(statsQuery.data?.collectionCounts, 6),
    [statsQuery.data?.collectionCounts],
  );
  const topComponents = useMemo(
    () =>
      Object.entries(statsQuery.data?.componentCounts ?? {})
        .map(([label, value]) => ({ label, value }))
        .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
        .slice(0, 6),
    [statsQuery.data?.componentCounts],
  );

  if (statsQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading dashboard...</div>;
  }

  if (statsQuery.isError || !statsQuery.data) {
    return <div className="text-sm text-red-500">Không tải được dashboard.</div>;
  }

  const stats = statsQuery.data;
  const coverageRatio = stats.totalAssets > 0 ? (stats.pricedAssets / stats.totalAssets) * 100 : 0;
  const recentUploads = stats.recentUploads ?? [];
  const priceDistribution = stats.priceDistribution ?? { min: 0, max: 0 };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="Một màn hình ngắn gọn để theo dõi tình trạng catalog, pricing coverage và những thay đổi gần đây."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void statsQuery.refetch();
            }}
            disabled={statsQuery.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${statsQuery.isFetching ? "animate-spin" : ""}`} />
            Reload
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Catalog assets"
          value={numberFormatter.format(stats.totalAssets)}
          hint={`${numberFormatter.format(stats.collections)} collections đang hiện diện trong catalog.`}
          icon={<Boxes className="h-4 w-4" />}
        />
        <MetricCard
          label="Pricing coverage"
          value={formatPercent(coverageRatio)}
          hint={`${numberFormatter.format(stats.pricedAssets)} đã gắn giá, còn ${numberFormatter.format(stats.unpricedAssets)} chưa cấu hình.`}
          icon={<DollarSign className="h-4 w-4" />}
        />
        <MetricCard
          label="Collections"
          value={numberFormatter.format(stats.collections)}
          hint={`Male ${numberFormatter.format(stats.genderCounts.male || 0)} / Female ${numberFormatter.format(stats.genderCounts.female || 0)}`}
          icon={<Layers3 className="h-4 w-4" />}
        />
        <MetricCard
          label="Average price"
          value={formatRenderPrice(stats.averagePrice || 0)}
          hint={`Min ${formatRenderPrice(priceDistribution.min || 0)} / Max ${formatRenderPrice(priceDistribution.max || 0)}`}
          icon={<Tags className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
        <Card className="border-border/80 bg-card/80">
          <CardHeader>
            <CardTitle>Recent uploads</CardTitle>
            <CardDescription>
              Danh sách ngắn các asset vừa đi vào catalog, ưu tiên metadata có ý nghĩa thay vì tên file thô.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentUploads.length === 0 ? (
              <EmptyState
                title="Chưa có recent uploads"
                description="Khi pipeline đẩy asset mới vào hệ thống, danh sách này sẽ hiện bản ghi gần nhất."
                className="p-6"
              />
            ) : (
              recentUploads.map((asset) => (
                <div
                  key={asset.id}
                  className="flex items-center gap-4 rounded-2xl border border-border/80 bg-muted/20 p-3"
                >
                  <div className="h-16 w-16 flex-none overflow-hidden rounded-xl border border-border/80 bg-muted">
                    <img
                      src={asset.url}
                      alt={getRecentUploadTitle(asset)}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {getRecentUploadTitle(asset)}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatRenderCollectionLabel(asset.collectionName)} • {asset.componentType} {asset.componentId} • drawable {asset.drawableId} • texture {asset.textureId}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <p>{formatRenderPrice(asset.price || 0)}</p>
                    <p className="uppercase">{asset.priceType || "money"}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6">
          <RankingList
            title="Top collections"
            description="Những collection đang chiếm nhiều asset nhất trong catalog hiện tại."
            items={topCollections}
          />
          <RankingList
            title="Top components"
            description="Các bucket component/prop có mật độ asset cao nhất."
            items={topComponents}
          />
        </div>
      </div>
    </div>
  );
}
